import { FeatureFlags } from '@kindo/universal';
import { useFlag } from '@unleash/nextjs';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import SettingsSection from '../../SettingsSection';

import {
  Button,
  ButtonType,
  Icon,
  IconButton,
  IconButtonType,
  Table,
  Typography,
  TypographyFont,
  TypographySize
} from '~/components/core';
import { useIntegrationConnections, useNangoConnect } from '~/hooks';

const NativeIntegrationsSettingsTab = () => {
  const [integrationConnectionIdToDelete, setIntegrationConnectionIdToDelete] =
    useState('');

  const {
    integrationConnectionsData,
    authorizedIntegrationConfigs,
    refetchIntegrationConnections,
    deleteIntegrationConnection
  } = useIntegrationConnections();

  const { connectNango: connect } = useNangoConnect({
    onSuccess: () => refetchIntegrationConnections
  });

  const integrationConnections = integrationConnectionsData ?? [];

  const useNativeIntegrationsProvider = useFlag(
    FeatureFlags.NATIVE_INTEGRATION_CREDENTIALS
  );

  const confirmButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset state when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        integrationConnectionIdToDelete &&
        confirmButtonRef.current &&
        !confirmButtonRef.current.contains(event.target as Node)
      ) {
        setIntegrationConnectionIdToDelete('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [integrationConnectionIdToDelete]);

  const renderActionButton = (integrationConnectionId: string) => {
    if (integrationConnectionIdToDelete === integrationConnectionId) {
      return (
        <div ref={confirmButtonRef}>
          <Button
            endIcon={Icon.CHECK}
            key={`${integrationConnectionId}-delete`}
            label="Confirm delete"
            onClick={() => deleteIntegrationConnection(integrationConnectionId)}
            type={ButtonType.OUTLINED}
            typographySize={TypographySize.X_SMALL}
          />
        </div>
      );
    }
    return (
      <IconButton
        highlightOnHover
        icon={Icon.TRASH}
        key={`${integrationConnectionId}-delete`}
        onClick={() =>
          setIntegrationConnectionIdToDelete(integrationConnectionId)
        }
        type={IconButtonType.NONE}
      />
    );
  };

  if (!useNativeIntegrationsProvider) return null;
  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid="native-integrations-container"
    >
      <div
        className="bg-blue-chat-background flex rounded-lg p-8"
        data-testid="connect-new-nango-container"
      >
        <SettingsSection title="Connect a New Integration Natively">
          <div className="pb-2 pt-4">
            <Button
              data-id="nango_connect"
              disabled={authorizedIntegrationConfigs?.length === 0}
              endIcon={Icon.LINK}
              label="Connect an integration"
              onClick={connect}
              type={ButtonType.SOLID_COLOR}
            />
          </div>
        </SettingsSection>
      </div>
      <div
        className="bg-blue-chat-background flex rounded-lg p-8"
        data-testid="connect-new-nango-container"
      >
        <SettingsSection title="Natively Connected Integrations">
          <Table
            columns={[
              {
                title: 'Name',
                width: 'w-[35%]'
              },
              {
                title: 'Auth Type',
                width: 'w-[20%]'
              },
              {
                title: 'Created At',
                width: 'w-[20%]'
              },
              {
                title: '',
                width: 'w-[30%]',
                align: 'center'
              }
            ]}
            noRowsText="No connected integrations"
            rows={integrationConnections.map(
              ({
                integrationConfig,
                id: integrationConnectionId,
                createdAt
              }) => ({
                key: integrationConnectionId,
                cells: [
                  // Name
                  <div
                    className="flex flex-row items-center gap-3"
                    data-testid={`integration-name-${integrationConnectionId}`}
                    key={`${integrationConnectionId}-square-image`}
                  >
                    {integrationConfig.imageUrl && (
                      <Image
                        alt="Icon"
                        height={28}
                        src={integrationConfig.imageUrl}
                        width={28}
                      />
                    )}
                    <Typography
                      font={TypographyFont.HEADING}
                      size={TypographySize.SMALL}
                    >
                      {integrationConfig.displayName}
                    </Typography>
                  </div>,
                  // Auth Type
                  <Typography
                    data-testid={`integration-auth-type-${integrationConnectionId}`}
                    font={TypographyFont.HEADING}
                    key={`${integrationConnectionId}-integration-auth-type`}
                    size={TypographySize.X_SMALL}
                  >
                    {integrationConfig.authType ?? ''}
                  </Typography>,
                  // Created At
                  <Typography
                    data-testid={`integration-category-${integrationConnectionId}`}
                    font={TypographyFont.HEADING}
                    key={`${integrationConnectionId}-integration-category`}
                    size={TypographySize.X_SMALL}
                  >
                    {new Date(createdAt).toLocaleDateString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>,
                  // Delete Button
                  <>{renderActionButton(integrationConnectionId)}</>
                ]
              })
            )}
            showColumnHeaderBorder
          />
        </SettingsSection>
      </div>
    </div>
  );
};

export default NativeIntegrationsSettingsTab;
